import { useEffect, useRef, useState, type ReactNode, type ComponentType } from "react";
import ErrorBoundary from "./ErrorBoundary";

interface LazyOnViewProps {
  fallback?: ReactNode;
  rootMargin?: string;
  minHeight?: string;
  importFn: () => Promise<{ default: ComponentType<any> }>;
  componentProps?: Record<string, any>;
  anchorId?: string;
}

const SectionErrorPlaceholder = ({ minHeight }: { minHeight: string }) => (
  <div
    style={{ minHeight }}
    className="flex items-center justify-center text-muted-foreground text-sm"
    aria-hidden="true"
  >
    {/* Invisible placeholder keeps layout stable */}
  </div>
);

const LazyOnView = ({
  fallback,
  rootMargin = "200px",
  minHeight = "400px",
  importFn,
  componentProps = {},
  anchorId,
}: LazyOnViewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [Component, setComponent] = useState<ComponentType<any> | null>(null);
  const [importError, setImportError] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          importFn()
            .then((mod) => setComponent(() => mod.default))
            .catch((err) => {
              console.error("LazyOnView import failed:", err);
              setImportError(true);
            });
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [importFn, rootMargin]);

  const [keepMinHeight, setKeepMinHeight] = useState(true);

  useEffect(() => {
    if (Component) {
      const frame = requestAnimationFrame(() => setKeepMinHeight(false));
      return () => cancelAnimationFrame(frame);
    }
  }, [Component]);

  if (importError) return <SectionErrorPlaceholder minHeight={minHeight} />;

  if (Component)
    return (
      <div id={anchorId} style={keepMinHeight ? { minHeight } : undefined}>
        <ErrorBoundary fallback={<SectionErrorPlaceholder minHeight={minHeight} />}>
          <Component {...componentProps} />
        </ErrorBoundary>
      </div>
    );

  return (
    <div ref={ref} id={anchorId} style={{ minHeight }}>
      {fallback ?? null}
    </div>
  );
};

export default LazyOnView;
